import JSZip from 'jszip';
import type { DocComment } from '@/state/types';

// ═══════════════════════════════════════════════════════════════
// Čtení komentářů z OOXML (Fáze 3).
//
// Mammoth komentáře úplně ignoruje, proto je čteme sami:
//   word/comments.xml          — obsah komentářů (autor, datum, text)
//   word/commentsExtended.xml  — vlákna (w15:paraIdParent) a vyřešení (w15:done)
//   word/document.xml          — kde v textu komentáře začínají/končí
//
// Vazba vláken je nepřímá: comments.xml má u odstavce w14:paraId, a
// commentsExtended.xml odkazuje právě přes tyhle paraId. Musíme si tedy
// namapovat paraId → id komentáře, abychom zjistili, kdo je čí rodič.
// ═══════════════════════════════════════════════════════════════

export interface CommentsResult {
  comments: DocComment[];
  /** ID komentářů, které mají v textu vyznačený rozsah (commentRangeStart). */
  rangedIds: number[];
  /**
   * Text komentované pasáže pro každé ID. Mammoth commentRangeStart/End
   * nevystavuje, takže rozsah dopočítáváme z document.xml a v naimportovaném
   * HTML pak pasáž najdeme podle textu.
   */
  rangeText: Record<number, string>;
}

export async function extractComments(buffer: ArrayBuffer): Promise<CommentsResult> {
  const empty: CommentsResult = { comments: [], rangedIds: [], rangeText: {} };
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(buffer);
  } catch {
    return empty;
  }

  const commentsXml = await readEntry(zip, 'word/comments.xml');
  if (!commentsXml) return empty;

  const extXml = await readEntry(zip, 'word/commentsExtended.xml');
  const docXml = await readEntry(zip, 'word/document.xml');

  // 1) Základní komentáře z comments.xml
  const parsed = parseComments(commentsXml);

  // 2) Vlákna a vyřešení z commentsExtended.xml (přes paraId)
  if (extXml) applyExtended(parsed.comments, parsed.paraIdToId, extXml);

  // 3) Rozsahy v textu
  const rangedIds = docXml ? findRangedIds(docXml) : [];
  const rangeText = docXml ? findRangeTexts(docXml) : {};

  return { comments: parsed.comments, rangedIds, rangeText };
}

async function readEntry(zip: JSZip, path: string): Promise<string | null> {
  const f = zip.file(path);
  if (!f) return null;
  try {
    return await f.async('string');
  } catch {
    return null;
  }
}

interface ParsedComments {
  comments: DocComment[];
  /** paraId prvního odstavce komentáře → id komentáře (pro vazbu vláken). */
  paraIdToId: Record<string, number>;
}

function parseComments(xml: string): ParsedComments {
  const comments: DocComment[] = [];
  const paraIdToId: Record<string, number> = {};

  // Každý komentář: <w:comment w:id=".." w:author=".." w:date=".." w:initials="..">…</w:comment>
  const re = /<w:comment\b([^>]*)>([\s\S]*?)<\/w:comment>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    const attrs = m[1];
    const body = m[2];

    const id = parseInt(attr(attrs, 'w:id') || '', 10);
    if (isNaN(id)) continue;

    const author = attr(attrs, 'w:author') || 'Neznámý autor';
    const initials = attr(attrs, 'w:initials') || initialsFrom(author);
    const dateRaw = attr(attrs, 'w:date') || '';
    const date = normalizeDate(dateRaw);

    // paraId odstavce komentáře — klíč pro vazbu vláken z commentsExtended.
    //
    // POZOR: Word odkazuje na paraId POSLEDNÍHO odstavce komentáře, ne
    // prvního. Víceodstavcový komentář má paraId u každého <w:p>, ale
    // commentsExtended.xml používá ten poslední. Když vezmeme první, vazba
    // vlákna se nenajde a odpověď se zobrazí jako samostatný komentář.
    //
    // Prefix namespace může kolísat (w14:, w:, …), proto ho nefixujeme.
    const paraIds = [...body.matchAll(/<w:p\b[^>]*?[\w]*:?paraId="([0-9A-Fa-f]+)"/gi)]
      .map((x) => x[1]);
    const paraIdMatch = paraIds.length ? paraIds[paraIds.length - 1] : null;
    if (paraIdMatch) paraIdToId[paraIdMatch.toUpperCase()] = id;

    comments.push({ id, text: extractText(body), author, initials, date });
  }

  return { comments, paraIdToId };
}

/** Doplní vlákna (parentId) a vyřešení (resolved) z commentsExtended.xml. */
function applyExtended(
  comments: DocComment[],
  paraIdToId: Record<string, number>,
  extXml: string,
): void {
  const byId = new Map(comments.map((c) => [c.id, c]));

  // Prefix namespace se může lišit (w15:, w16cex:, …) — hledáme bez ohledu na něj.
  const re = /<[\w]*:?commentEx\b([^>]*)\/?>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(extXml)) !== null) {
    const attrs = m[1];
    const paraId = (attrAny(attrs, 'paraId') || '').toUpperCase();
    const id = paraIdToId[paraId];
    if (id == null) continue;
    const c = byId.get(id);
    if (!c) continue;

    // Vlákno: paraIdParent ukazuje na odstavec kořenového komentáře.
    const parentParaId = (attrAny(attrs, 'paraIdParent') || '').toUpperCase();
    if (parentParaId) {
      const parentId = paraIdToId[parentParaId];
      if (parentId != null && parentId !== id) c.parentId = parentId;
    }

    // Vyřešeno: done="1"
    const done = attrAny(attrs, 'done');
    if (done === '1' || done === 'true') c.resolved = true;
  }
}

/** ID komentářů, které mají v těle dokumentu commentRangeStart. */
function findRangedIds(docXml: string): number[] {
  const ids = new Set<number>();
  const re = /<w:commentRangeStart\b[^>]*w:id="(\d+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(docXml)) !== null) {
    ids.add(parseInt(m[1], 10));
  }
  return [...ids];
}

/**
 * Pro každé ID komentáře vytáhne TEXT komentované pasáže — vše mezi
 * <w:commentRangeStart w:id="N"/> a <w:commentRangeEnd w:id="N"/>.
 * Rozsahy se mohou překrývat, proto hledáme každý zvlášť podle ID.
 */
function findRangeTexts(docXml: string): Record<number, string> {
  const out: Record<number, string> = {};
  const startRe = /<w:commentRangeStart\b[^>]*w:id="(\d+)"[^>]*\/>/g;
  let m: RegExpExecArray | null;
  while ((m = startRe.exec(docXml)) !== null) {
    const id = parseInt(m[1], 10);
    const startIdx = m.index + m[0].length;
    // Konec téhož ID (může být za víc runy i odstavci).
    const endRe = new RegExp(`<w:commentRangeEnd\\b[^>]*w:id="${id}"[^>]*/>`);
    const rest = docXml.slice(startIdx);
    const endMatch = rest.match(endRe);
    if (!endMatch || endMatch.index === undefined) continue;
    const inner = rest.slice(0, endMatch.index);

    // Prostý text z <w:t> uvnitř rozsahu.
    const parts: string[] = [];
    const tRe = /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/g;
    let t: RegExpExecArray | null;
    while ((t = tRe.exec(inner)) !== null) parts.push(unescapeXml(t[1]));
    const text = parts.join('').trim();
    if (text) out[id] = text;
  }
  return out;
}

/** Vytáhne prostý text ze všech <w:t> uvnitř komentáře. */
function extractText(body: string): string {
  const parts: string[] = [];
  const re = /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    parts.push(unescapeXml(m[1]));
  }
  // Odstavce v komentáři oddělíme mezerou (náš model drží prostý text).
  return parts.join('').trim() || '(prázdný komentář)';
}

function attr(attrs: string, name: string): string | null {
  const m = attrs.match(new RegExp(`\\b${name.replace(':', '\\:')}="([^"]*)"`));
  return m ? m[1] : null;
}

/**
 * Přečte atribut bez ohledu na prefix namespace — Word používá w14:, w15:,
 * ale i novější w16cex: apod. Hledáme tedy `cokoli:name="…"` i `name="…"`.
 */
function attrAny(attrs: string, localName: string): string | null {
  const m = attrs.match(new RegExp(`(?:^|\\s)(?:[\\w]+:)?${localName}="([^"]*)"`, 'i'));
  return m ? m[1] : null;
}

/** Word datum → ISO string. Neplatné/chybějící → teď. */
function normalizeDate(raw: string): string {
  if (!raw) return new Date().toISOString();
  const d = new Date(raw);
  return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

function unescapeXml(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}
